import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Card,
  message,
  Typography,
  Space,
  Upload,
  Avatar,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  UploadOutlined,
  UserOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { InputNumber } from "antd";


const { Title, Text } = Typography;
const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL;

// ==========================
// Helpers: Edad y Etapa
// ==========================
const calcularEdadTexto = (fechaNacimiento) => {
  if (!fechaNacimiento) return "";

  const nacimiento = dayjs(fechaNacimiento);
  const hoy = dayjs();

  if (!nacimiento.isValid()) return "";

  let años = hoy.diff(nacimiento, "year");
  let resto = nacimiento.add(años, "year");

  let meses = hoy.diff(resto, "month");
  resto = resto.add(meses, "month");

  let dias = hoy.diff(resto, "day");

  const partes = [];
  if (años > 0) partes.push(`${años} año${años !== 1 ? "s" : ""}`);
  if (meses > 0) partes.push(`${meses} mes${meses !== 1 ? "es" : ""}`);
  if (dias > 0 || partes.length === 0)
    partes.push(`${dias} día${dias !== 1 ? "s" : ""}`);

  return partes.join(", ");
};

const calcularEtapaProductiva = (fechaNacimiento, sexo) => {
  if (!fechaNacimiento) return "";
  const nacimiento = dayjs(fechaNacimiento);
  const hoy = dayjs();

  if (!nacimiento.isValid()) return "";

  const mesesTotal = hoy.diff(nacimiento, "month");

  if (mesesTotal < 6) return "Cría";
  if (mesesTotal < 14) return "Levante";
  if (mesesTotal < 24) return "Desarrollo";

  if (sexo === "Hembra") return "Vaca";
  if (sexo === "Macho") return "Toro";

  return "Adulto";
};

// Normalizar enums que vienen como número o como texto
const normalizarEnum = (valor, mapaNumeroATexto) => {
  if (valor === null || valor === undefined) return undefined;
  if (typeof valor === "number") return mapaNumeroATexto[valor];
  return valor;
};

// Normalizar listas para soportar:
// - Array normal: [...]
//
// - Preserve: { "$values": [...] }
const normalizarLista = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.$values)) return data.$values;
  return [];
};

const FrmAnimalForm = () => {
  const [form] = Form.useForm();
  const [formRaza] = Form.useForm();

  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const esNuevo = !id;
  const esSoloLectura = !esNuevo && !location.pathname.endsWith("/editar");

  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [razas, setRazas] = useState([]);
  const [madres, setMadres] = useState([]);
  const [padres, setPadres] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);

  const [edadTexto, setEdadTexto] = useState("");
  const [etapaProductiva, setEtapaProductiva] = useState("");

  const [fotoPreview, setFotoPreview] = useState(null);

  // Modal crear raza
  const [modalRazaOpen, setModalRazaOpen] = useState(false);
  const [guardandoRaza, setGuardandoRaza] = useState(false);

  // ==========================
  // Cargar combos
  // ==========================
  const cargarCombos = async () => {
    try {
      const [respMadres, respPadres, respCC] = await Promise.all([
        axios.get(`${API_URL}/Animal/listaMadres`),
        axios.get(`${API_URL}/Animal/listaPadres`),
        axios.get(`${API_URL}/CentroCosto`),
      ]);

      setMadres(normalizarLista(respMadres.data));
      setPadres(normalizarLista(respPadres.data));
      setCentrosCosto(normalizarLista(respCC.data));
    } catch (error) {
      console.error("Error cargando listas:", error);
      message.error("Error cargando listas de madres/padres/centros de costo.");
    }
  };

 const cargarRazas = async () => {
  try {
    const resp = await axios.get(`${API_URL}/RazaAnimal`);

    const lista = normalizarLista(resp.data);

    setRazas(lista);
  } catch (error) {
    console.error("Error cargando razas:", error);
    message.error("Error cargando razas.");
    setRazas([]);
  }
};


  // ==========================
  // Cargar Animal (editar/ver)
  // ==========================
  const cargarAnimal = async (animalId) => {
    try {
      setCargando(true);
      const resp = await axios.get(`${API_URL}/Animal/${animalId}`);
      const data = resp.data;

      const sexoTexto = normalizarEnum(data.sexo, { 1: "Macho", 2: "Hembra" });
      const estadoTexto = normalizarEnum(data.estado, {
        1: "Activo",
        2: "Vendido",
        3: "Muerto",
        4: "Transferido",
      });
      const origenTexto = normalizarEnum(data.origen, {
        1: "Nacimiento",
        2: "Compra",
        3: "Transferencia",
      });

      const fechaNac = data.fechaNacimiento ? dayjs(data.fechaNacimiento) : null;
      const fechaIng = data.fechaIngreso ? dayjs(data.fechaIngreso) : null;

      const edad = fechaNac ? calcularEdadTexto(fechaNac) : "";
      const etapa = calcularEtapaProductiva(fechaNac, sexoTexto);

      setEdadTexto(edad);
      setEtapaProductiva(etapa);

      form.setFieldsValue({
        arete: data.arete,
        nombre: data.nombre,
        especie: normalizarEnum(data.especie, { 1: "Bovino", 2: "Aviar", 3: "Canino" }) ?? "Bovino",
        razaId: data.razaId ?? null,
        lineaGenetica: data.lineaGenetica,
        sexo: sexoTexto,
        fechaNacimiento: fechaNac,
        fechaIngreso: fechaIng,
        origen: origenTexto,
        madreId: data.madreId || null,
        padreId: data.padreId || null,
        padreExternoNombre: data.padreExternoNombre,
        codigoSemen: data.codigoSemen,
        estado: estadoTexto,
        centroCostoId: data.centroCostoId || null,
        etapaProductivaCache: etapa || data.etapaProductivaCache,
        pesoActual: data.pesoActual ?? null,
        observaciones: data.observaciones || "",
      });
    } catch (error) {
      console.error("Error cargando animal:", error);
      message.error("Error cargando información del animal.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await Promise.all([cargarCombos(), cargarRazas()]);

      if (!esNuevo) {
        await cargarAnimal(id);
      } else {
        form.setFieldsValue({
          origen: "Compra",
          estado: "Activo",
          especie: "Bovino",
        });
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ==========================
  // Recalcular edad/etapa
  // ==========================
  const manejarCambiosFormulario = (changedValues, allValues) => {
    const fechaNacimiento =
      changedValues.fechaNacimiento || allValues.fechaNacimiento;
    const sexo = changedValues.sexo || allValues.sexo;

    if (fechaNacimiento) {
      const edad = calcularEdadTexto(fechaNacimiento);
      const etapa = calcularEtapaProductiva(fechaNacimiento, sexo);

      setEdadTexto(edad);
      setEtapaProductiva(etapa);

      form.setFieldsValue({ etapaProductivaCache: etapa });
    } else {
      setEdadTexto("");
      setEtapaProductiva("");
      form.setFieldsValue({ etapaProductivaCache: "" });
    }
  };

  // ==========================
  // Guardar Animal
  // ==========================
  const onFinish = async (values) => {
    try {
      setGuardando(true);

      if (values.fechaNacimiento && values.fechaIngreso) {
        if (values.fechaIngreso.isBefore(values.fechaNacimiento, "day")) {
          message.error(
            "La fecha de ingreso no puede ser menor que la fecha de nacimiento."
          );
          return;
        }
      }

      const payload = {
        arete: values.arete,
        nombre: values.nombre,
        especie: values.especie || "Bovino",
        razaId: values.razaId,
        lineaGenetica: values.lineaGenetica,
        sexo: values.sexo,
        fechaNacimiento: values.fechaNacimiento
          ? values.fechaNacimiento.toISOString()
          : null,
        fechaIngreso: values.fechaIngreso
          ? values.fechaIngreso.toISOString()
          : null,
        origen: values.origen,
        madreId: values.madreId || null,
        padreId: values.padreId || null,
        padreExternoNombre: values.padreExternoNombre,
        codigoSemen: values.codigoSemen,
        estado: values.estado,
        centroCostoId: values.centroCostoId || null,
        etapaProductivaCache: etapaProductiva || values.etapaProductivaCache || "",
        observaciones: values.observaciones || "",

        partosPrevios: values.partosPrevios ?? null,
        pesoActual: values.pesoActual,
        observacionPeso: values.observaciones || null,
      };


      if (esNuevo) {
        await axios.post(`${API_URL}/Animal`, payload);
        message.success("Animal creado correctamente.");
      } else {
        await axios.put(`${API_URL}/Animal/${id}`, payload);
        message.success("Animal actualizado correctamente.");
      }

      navigate("/dashboard/ganado/animales");
    } catch (error) {
      console.error("Error guardando animal:", error);
      message.error("Error guardando la información del animal.");
    } finally {
      setGuardando(false);
    }
  };

  // ==========================
  // Foto (solo UI)
  // ==========================
  const beforeUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setFotoPreview(e.target.result);
    reader.readAsDataURL(file);
    return false;
  };

  // ==========================
  // Modal: Crear Raza
  // ==========================
  const abrirModalRaza = () => {
    formRaza.resetFields();
    setModalRazaOpen(true);
  };

  const cerrarModalRaza = () => {
    setModalRazaOpen(false);
  };

  const guardarNuevaRaza = async () => {
  try {
    const values = await formRaza.validateFields();
    const nombre = values.nombre.trim();

    // 🔴 VALIDACIÓN DUPLICADO (FRONT)
    const existe = razas.some(
      r => r.nombre.toLowerCase() === nombre.toLowerCase()
    );

    if (existe) {
      message.error("La raza ya existe.");
      return;
    }

    setGuardandoRaza(true);

    const resp = await axios.post(`${API_URL}/RazaAnimal`, { nombre });
    const nueva = resp.data;

    message.success("Raza creada correctamente.");

    await cargarRazas();
    form.setFieldsValue({ razaId: nueva.id });

    setModalRazaOpen(false);
  } catch (error) {
    if (error?.errorFields) return;

    console.error("Error creando raza:", error);
    message.error("Error creando la raza.");
  } finally {
    setGuardandoRaza(false);
  }
};


  const titulo = esNuevo
    ? "Nuevo Animal"
    : esSoloLectura
    ? "Detalle de Animal"
    : "Editar Animal";

  return (
    <div style={{ padding: 16 }}>
      <Card loading={cargando}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/dashboard/ganado/animales")}
          >
            Volver
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {titulo}
          </Title>
          {!esNuevo && (
            <>
              <Button onClick={() => navigate(`/dashboard/ganado/animales/${id}/pesos`)}>
                Pesos
              </Button>
              <Button onClick={() => navigate(`/dashboard/ganado/animales/${id}/salud`)}>
                Salud
              </Button>
              <Button onClick={() => navigate(`/dashboard/ganado/animales/${id}/leche`)}>
                Leche
              </Button>
            </>
          )}
        </Space>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Text strong>Edad: </Text>
            <Text>{edadTexto || "-"}</Text>
          </Col>
          <Col xs={24} md={12}>
            <Text strong>Etapa productiva: </Text>
            <Text>{etapaProductiva || "-"}</Text>
          </Col>
        </Row>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={manejarCambiosFormulario}
          disabled={esSoloLectura}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Arete"
                name="arete"
                rules={[{ required: true, message: "El arete es obligatorio." }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Nombre"
                name="nombre"
                rules={[
                  { required: true, message: "El nombre es obligatorio." },
                  { min: 2, message: "El nombre debe tener al menos 2 caracteres." }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label="Especie"
                name="especie"
                rules={[{ required: true, message: "La especie es obligatoria." }]}
              >
                <Select placeholder="Seleccione especie">
                  <Option value="Bovino">Bovino</Option>
                  <Option value="Aviar">Aviar</Option>
                  <Option value="Canino">Canino</Option>
                </Select>
              </Form.Item>
            </Col>

            {/* Raza + Botón + */}
            <Col xs={24} md={8}>
              <Form.Item
                label="Raza"
                required
                style={{ marginBottom: 0 }}
              >
                <Row gutter={8} align="middle">
                  <Col flex="auto">
                    <Form.Item
                      name="razaId"
                      rules={[{ required: true, message: "La raza es obligatoria" }]}
                      style={{ marginBottom: 0 }}
                    >
                     <Select
                          showSearch
                          placeholder="Seleccione la raza"
                          optionFilterProp="children"
                          notFoundContent={razas.length === 0 ? "Cargando razas..." : null}
                          filterOption={(input, option) =>
                            (option?.children ?? "")
                              .toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {razas.map((r) => (
                            <Option key={r.id} value={r.id}>
                              {r.nombre}
                            </Option>
                          ))}
                        </Select>

                    </Form.Item>
                  </Col>

                  <Col>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={abrirModalRaza}
                      disabled={esSoloLectura}
                      title="Crear nueva raza"
                    />
                  </Col>
                  

                </Row>
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item label="Línea genética" name="lineaGenetica">
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label="Sexo"
                name="sexo"
                rules={[{ required: true, message: "El sexo es obligatorio." }]}
              >
                <Select placeholder="Seleccione sexo">
                  <Option value="Macho">Macho</Option>
                  <Option value="Hembra">Hembra</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item
                label="Fecha de nacimiento"
                name="fechaNacimiento"
                rules={[
                  { required: true, message: "La fecha de nacimiento es obligatoria." },
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item
                label="Fecha de ingreso"
                name="fechaIngreso"
                rules={[
                  { required: true, message: "La fecha de ingreso es obligatoria." },
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item
                label="Origen"
                name="origen"
                rules={[{ required: true, message: "El origen es obligatorio." }]}
              >
                <Select placeholder="Seleccione origen">
                  <Option value="Nacimiento">Nacimiento</Option>
                  <Option value="Compra">Compra</Option>
                  <Option value="Transferencia">Transferencia</Option>
                </Select>
              </Form.Item>
            </Col>

            {form.getFieldValue("sexo") === "Hembra" && (
              <Col xs={24} md={6}>
                <Form.Item
                  label="Partos previos"
                  name="partosPrevios"
                  tooltip="Número de partos antes de ingresar al sistema"
                  rules={[
                    { type: "number", min: 0, message: "No puede ser negativo" }
                  ]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    placeholder="Opcional"
                  />
                </Form.Item>
              </Col>
            )}


            <Col xs={24} md={6}>
              <Form.Item
                label="Estado"
                name="estado"
                rules={[{ required: true, message: "El estado es obligatorio." }]}
              >
                <Select placeholder="Seleccione estado">
                  <Option value="Activo">Activo</Option>
                  <Option value="Vendido">Vendido</Option>
                  <Option value="Muerto">Muerto</Option>
                  <Option value="Transferido">Transferido</Option>
                </Select>
              </Form.Item>

            </Col>

            <Col xs={24} md={6}>
              <Form.Item label="Madre" name="madreId">
                <Select allowClear placeholder="Seleccione madre">
                  {madres.map((m) => (
                    <Option key={m.id} value={m.id}>
                      {m.etiqueta || `${m.id}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item label="Padre" name="padreId">
                <Select allowClear placeholder="Seleccione padre">
                  {padres.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.etiqueta || `${p.id}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item label="Padre externo (nombre)" name="padreExternoNombre">
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
                    <Form.Item
                      label="Peso actual (kg)"
                      name="pesoActual"
                      rules={[{ required: true, message: "El peso actual es obligatorio." }]}
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item label="Código de semen" name="codigoSemen">
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item
                label="Centro de costo"
                name="centroCostoId"
                rules={[{ required: true, message: "El centro de costo es obligatorio." }]}
              >
                <Select placeholder="Seleccione centro de costo">
                  {centrosCosto.map((cc) => (
                    <Option key={cc.id} value={cc.id}>
                      {cc.nombre || cc.descripcion || `Centro ${cc.id}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

            </Col>

            <Col xs={24} md={6}>
              <Form.Item label="Etapa productiva" name="etapaProductivaCache">
                <Input readOnly />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Foto de perfil">
                <Space>
                  <Avatar size={64} src={fotoPreview} icon={!fotoPreview && <UserOutlined />} />
                  <Upload beforeUpload={beforeUpload} showUploadList={false}>
                    <Button icon={<UploadOutlined />}>Seleccionar foto</Button>
                  </Upload>
                </Space>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item label="Observaciones" name="observaciones">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>

          {!esSoloLectura && (
            <Form.Item style={{ marginTop: 16 }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={guardando}
                >
                  Guardar
                </Button>
                <Button onClick={() => navigate("/dashboard/ganado/animales")}>
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          )}
        </Form>
      </Card>

      {/* MODAL CREAR RAZA */}
      <Modal
        title="Nueva Raza"
        open={modalRazaOpen}
        onCancel={cerrarModalRaza}
        closable={true}        // X visible
        destroyOnClose={true}
        okText="Guardar"
        cancelText="Cerrar"
        confirmLoading={guardandoRaza}
        onOk={guardarNuevaRaza}
      >
        <Form form={formRaza} layout="vertical">
          <Form.Item
            label="Nombre de la raza"
            name="nombre"
            rules={[{ required: true, message: "El nombre es obligatorio." }]}
          >
            <Input placeholder="Ej: Gyr, Brahman, Holstein..." maxLength={60} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FrmAnimalForm;
